import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { InputAdornment, Typography } from '@mui/material';
import styles from 'new/elements/NexsetSchemaViewer/NexsetSchemaViewer.module.scss';
import { IconButton } from 'new/elements/IconButton';
import { Select } from 'new/elements/Select';
import { ReactComponent as CommentCodeIcon } from 'new/components/icons/commentCode.svg';
import { ReactComponent as LineWidthIcon } from 'new/components/icons/lineWidth.svg';
import { ReactComponent as DownloadIcon } from 'new/components/icons/download.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/refresh.svg';
import { ReactComponent as SearchIcon } from 'new/components/icons/search.svg';
import React, { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import { FLOW_TYPES_WITHOUT_LIVE_SAMPLES } from 'js/constants/pipelineConstants';
import useEntities from 'new/state/entities/repository/useEntities';
import { TextField } from 'new/elements/TextField';
import { MenuDownload } from 'new/elements/NexsetSchemaViewer/MenuDownload';
import { Nexset } from 'new/entities/Nexset';

const numSamples = [
  {
    title: '5 cached samples',
    value: 'cached-5',
    count: 5,
    live: false,
  },
  {
    title: '10 cached samples',
    value: 'cached-10',
    count: 10,
    live: false,
  },
  {
    title: '15 cached samples',
    value: 'cached-15',
    count: 15,
    live: false,
  },
  {
    title: '20 cached samples',
    value: 'cached-20',
    count: 20,
    live: false,
  },
  {
    title: '5 live samples',
    value: 'live-5',
    count: 5,
    live: true,
  },
  {
    title: '10 live samples',
    value: 'live-10',
    count: 10,
    live: true,
  },
  {
    title: '25 live samples',
    value: 'live-25',
    count: 25,
    live: true,
  },
  {
    title: '50 live samples',
    value: 'live-50',
    count: 50,
    live: true,
  },
  {
    title: '100 live samples',
    value: 'live-100',
    count: 100,
    live: true,
  },
];

type Props = {
  viewControlValue: string;
  numSamplesValue: string;
  onSamplesChange: (sample: any) => void;
  onSearchChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRefresh: () => void;
  onViewControlChange: (event: SyntheticEvent<Element, Event>, value: any) => void;
  nexset: Nexset;
  hideTitle?: boolean;
};

export function RecipeBuilderControls({
  viewControlValue,
  numSamplesValue,
  onSamplesChange,
  onRefresh,
  onSearchChange,
  nexset,
  onViewControlChange,
  hideTitle = false,
}: Props) {
  const { data: sources } = useEntities('sources');

  // Get the flow type from the data source associated with this nexset
  const flowType = useMemo(() => {
    // First check if the nexset itself has a flow_type
    if ((nexset as any)?.flow_type) {
      return (nexset as any).flow_type;
    }

    // If not, check the associated source
    if (nexset?.data_source_id) {
      const source = sources[nexset.data_source_id];
      return source?.flow_type || null;
    }

    return null;
  }, [(nexset as any)?.flow_type, nexset?.data_source_id, sources]);

  // Simple filtering: show all options for supported flow types, only cached for unsupported
  const availableSampleOptions = useMemo(() => {
    if (!flowType || !FLOW_TYPES_WITHOUT_LIVE_SAMPLES.includes(flowType)) {
      // For flow types that support live samples, show all options
      return numSamples;
    }

    // For flow types that don't support live samples, only show cached options
    return numSamples.filter((sample) => !sample.live);
  }, [flowType]);

  const viewControls = [
    {
      label: 'Attributes as Rows',
      icon: <LineWidthIcon />,
      value: SchemaEditorViewModeType.TABLE,
    },
    {
      label: 'Attributes as JSON',
      icon: <CommentCodeIcon />,
      value: SchemaEditorViewModeType.CODE,
    },
  ] as ControlProps[];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const onClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    onViewControlChange(null, viewControls[0].value);
  }, []);

  const handleSamplesChange = (e) => {
    const { value } = e.target;
    const selectedSample = availableSampleOptions.find((n) => n.value === value);
    if (selectedSample) {
      onSamplesChange(selectedSample);
    }
  };

  return (
    <div className={styles.controlsContainer}>
      {!hideTitle && <Typography variant="subtitle1">Schema & samples</Typography>}
      <div className={styles.controls}>
        <TextField
          size="small"
          placeholder="Search"
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.controlAction}>
          <IconButton onClick={onRefresh}>
            <RefreshIcon />
          </IconButton>
        </div>
        <div className={styles.controlAction}>
          <IconButton onClick={onClickDownload}>
            <DownloadIcon />
          </IconButton>
          <MenuDownload nexset={nexset} anchorEl={anchorEl} open={open} onClose={handleClose} />
        </div>
        <Select
          size="small"
          items={availableSampleOptions}
          onChange={handleSamplesChange}
          value={numSamplesValue || availableSampleOptions[0]?.value}
        />
        <SegmentedControl
          iconOnly
          controls={viewControls}
          defaultValue={viewControls[0].value}
          value={viewControlValue || viewControls[0].value}
          onControlChange={onViewControlChange}
        />
      </div>
    </div>
  );
}
